import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useWindowDimensions } from 'react-native';
import { useFocusEffect, useNavigation } from '@react-navigation/native';
import { NCFileVersion as NCFileVersionType } from '@interfaces/search';
import { isPhone, isPhoneNative, isWeb, navigateTo, Popup, injectConditionalObject } from '@helpers';
import { trackEvent, TRACKING_EVENTS } from '@services/tracking';
import { getLinkingModalConfig } from '@helpers';
import SendingToMachineSelectMachineStep from '@v2/pages/common/modals/ncFile/SendingToMachineSelectMachineStep';
import { scale } from '@components/frame';
import { RightColumnLevel, UseManageNCFileVersionsProps } from '@components/ncFileVersions/types';
import { useSelector } from 'react-redux';
import { getNCFileById } from '@reducers/manufacture';
import { setNcFileData, setSelectedNcFileVersionId } from '@reducers/details';
import { applyNcFileVersionMasterDataChanges } from '@reducers/admin';
import { selectMachines } from '@reducers/machines';
import { useNavigationCheck } from '@v2/pages/machines/hooks/useNavigationCheck';
import { machineEventCycleStatistics } from '@reducers/statistics';
import { LIMIT } from '@v2/pages/common/components/NCFileBatchEvents/constants';
import { useAppDispatch } from '@store/hooks/useAppDispatch';
import { useUploadNcFileVersion } from './useUploadNcFileVersion';
import { NCFILE_MOBILE_BREAKPOINT, NCFILE_TWO_COLUMN_BREAKPOINT } from '@components/ncFileVersions/constants';
import { LIST_WIDTH } from '@v2/pages/common/constants/base.constants';
import { toggleVersionInSelection } from '@components/ncFileVersions/helpers/toggleVersionInSelection';
import { showIdenticalVersionsAlert } from '@components/ncFileVersions/helpers/showIdenticalVersionsAlert';
import { replaceVersionInPair } from '@components/ncFileVersions/helpers/replaceVersionInPair';
import getPcsCount from '@components/ncFileVersions/helpers/getPcsCount';
import {
  addCyclePcsCounts,
  arePcsCountsEqual,
  createInitialVersionPcsCounts,
  mergePcsCountsIntoVersions,
  NCFileVersionPcsCounts,
} from '@components/ncFileVersions/helpers/ncFileVersionPcsCounts';

export const useManageNcFileVersions = ({
  ncFile,
  ncFileId,
  machineId,
  ncFileVersionIdToSelect,
  shouldLoadNcFile = false,
  showOnlyActiveVersionsInitialState = true,
  C,
  enableResponsiveTwoColumnView = false,
  twoColumnBreakpoint = NCFILE_TWO_COLUMN_BREAKPOINT,
  mobileBreakpoint = NCFILE_MOBILE_BREAKPOINT,
  forceTwoColumnOnNativeTablet = false,
  twoColumnView = false,
}: UseManageNCFileVersionsProps) => {
  const dispatch = useAppDispatch();
  const navigation = useNavigation<any>();

  const { safeIsMachinesScreenCheck } = useNavigationCheck();
  const { onUploadNewVersion } = useUploadNcFileVersion({ ncFile });

  const machines = useSelector(selectMachines);

  const { width: viewportWidth = 0 } = useWindowDimensions();
  const wasTwoColumnRef = useRef<boolean>(twoColumnView);
  const isMobileWebViewport = enableResponsiveTwoColumnView && isWeb && viewportWidth > 0 && viewportWidth <= mobileBreakpoint;

  const [versionPcsCounts, setVersionPcsCounts] = useState<NCFileVersionPcsCounts>({});

  // Comparison mode state
  const [comparisonMode, setComparisonMode] = useState<boolean>(false);
  const [versionsToCompare, setVersionsToCompare] = useState<NCFileVersionType[]>([]);
  const [showDiffModal, setShowDiffModal] = useState<boolean>(false);

  const versionsWithPcs = useMemo(() => mergePcsCountsIntoVersions(ncFile?.versions, versionPcsCounts), [ncFile?.versions, versionPcsCounts]);

  const favorite = versionsWithPcs.find((i) => i.isFavorite);
  const latest = versionsWithPcs[0];
  const favoriteOrLatest = favorite ?? latest;
  const machineWhichRanFavoriteOrLatest = machines?.[0];

  const [selectedVersion, setSelectedVersion] = useState<NCFileVersionType | undefined>(favoriteOrLatest);
  const [showOnlyActiveVersions, setShowOnlyActiveVersions] = useState<boolean>(showOnlyActiveVersionsInitialState);
  const [rightColumnLevel, setRightColumnLevel] = useState<RightColumnLevel>(RightColumnLevel.Info);

  // Sync selected version ID to Redux for NCFileModal (MoreButton menu)
  useEffect(() => {
    dispatch(setSelectedNcFileVersionId(selectedVersion?.ncFileVersionId ?? null));
  }, [selectedVersion?.ncFileVersionId, dispatch]);

  // Reset comparison-related state when ncFile context changes (e.g., switching machines)
  useEffect(() => {
    setComparisonMode(false);
    setVersionsToCompare([]);
    setShowDiffModal(false);
    setRightColumnLevel(RightColumnLevel.Info);
    setSelectedVersion(favoriteOrLatest);
  }, [ncFile?.ncFileId, favoriteOrLatest?.ncFileVersionId, machineId]);

  useFocusEffect(
    useCallback(() => {
      if (!ncFile?.ncFileId || !ncFile?.versions?.length) {
        setVersionPcsCounts({});
        return;
      }

      const shouldLoadPcs = ncFile.versions.some((version) => typeof getPcsCount(version) !== 'number');
      if (!shouldLoadPcs) {
        setVersionPcsCounts({});
        return;
      }

      const controller = new AbortController();
      let isActive = true;

      const fetchCounts = async () => {
        try {
          let completeCounts = createInitialVersionPcsCounts(ncFile.versions);
          let pageNumber = 1;
          let hasMore = true;

          while (hasMore) {
            const response = await dispatch(
              machineEventCycleStatistics(
                {
                  ncFileId: ncFile.ncFileId,
                  pagination: {
                    includeCount: false,
                    pageNumber,
                    pageSize: LIMIT,
                  },
                },
                controller.signal
              )
            );

            if (!isActive) return;

            const cycles = response?.cycles ?? [];
            completeCounts = addCyclePcsCounts(completeCounts, cycles);
            hasMore = cycles.length >= LIMIT;
            pageNumber++;
          }

          setVersionPcsCounts((prev) => (arePcsCountsEqual(prev, completeCounts) ? prev : completeCounts));

          const nextVersions = mergePcsCountsIntoVersions(ncFile.versions, completeCounts);
          const hasChanges = nextVersions.some((version, index) => version !== ncFile.versions[index]);
          if (hasChanges) {
            dispatch(
              setNcFileData({
                ...ncFile,
                versions: nextVersions,
              })
            );
          }
        } catch (error) {
          if (!controller.signal.aborted) {
            console.error('Failed to load pcs counts for NC file versions', error);
            setVersionPcsCounts({});
          }
        }
      };

      fetchCounts();

      return () => {
        isActive = false;
        controller.abort();
      };
    }, [dispatch, ncFile?.ncFileId, ncFile?.versions])
  );

  const otherVersions = versionsWithPcs.filter(
    (i) => i.ncFileVersionId !== favoriteOrLatest?.ncFileVersionId && (showOnlyActiveVersions ? i.isActive : true)
  );

  useFocusEffect(
    useCallback(() => {
      if (!shouldLoadNcFile || ncFileId == null) return;
      (async () => {
        const ncFileData = await dispatch(getNCFileById(ncFileId));
        dispatch(setNcFileData(ncFileData));
      })();
    }, [shouldLoadNcFile, ncFileId])
  );

  useFocusEffect(
    useCallback(() => {
      if (!ncFile) return;
      const versionToSelect = ncFileVersionIdToSelect ? versionsWithPcs.find((v) => v.ncFileVersionId === +ncFileVersionIdToSelect) : null;
      setSelectedVersion(versionToSelect ?? favoriteOrLatest);
    }, [ncFile?.ncFileId, ncFileVersionIdToSelect, versionsWithPcs, favoriteOrLatest])
  );

  useEffect(() => {
    if (!selectedVersion) return;
    const updated = versionsWithPcs.find((v) => v.ncFileVersionId === selectedVersion.ncFileVersionId);
    if (updated && updated !== selectedVersion) {
      setSelectedVersion(updated);
    }
  }, [versionsWithPcs, selectedVersion?.ncFileVersionId]);

  useEffect(() => {
    setVersionsToCompare((prev) => {
      if (!prev.length) {
        return prev;
      }
      let changed = false;
      const mapped = prev.map((version) => {
        const updated = versionsWithPcs.find((v) => v.ncFileVersionId === version.ncFileVersionId);
        if (updated && updated !== version) {
          changed = true;
          return updated;
        }
        return version;
      });
      return changed ? mapped : prev;
    });
  }, [versionsWithPcs]);

  const onSelectVersion = (version: NCFileVersionType) => {
    trackEvent(TRACKING_EVENTS.NC_FILE, {
      action: 'versionSelected',
      ncFileId: String(ncFileId || ''),
      versionNumber: version?.sequenceNumber || 0,
    });
    if (isPhoneNative || isMobileWebViewport) {
      const params = {
        ncFileId: ncFile?.ncFileId,
        ncFileVersionId: version?.ncFileVersionId,
        showOnlyActiveVersionsInitialState,
      };
      if (safeIsMachinesScreenCheck) {
        navigation.navigate('Authorized', { screen: 'NCFileVersionDetails', params });
      } else {
        navigateTo('NCFileVersionDetails', params);
      }
    } else {
      setSelectedVersion(version);
    }
  };

  const onSendToMachine = () => {
    Popup({
      Component: (p: any) => (
        <SendingToMachineSelectMachineStep {...{ ...p, C, selectedNCFile: selectedVersion, favoriteNCFile: favorite, ncFileData: ncFile }} />
      ),
      ...getLinkingModalConfig(scale),
      ...injectConditionalObject(!isPhone, {
        width: scale(450),
        useFullHeight: false,
        ...injectConditionalObject(!isWeb, { noPhoneHeight: scale(450) }),
      }),
    });
  };

  const onToggleFavoriteVersion = async (version: NCFileVersionType) => {
    if (!ncFile || version.isFavorite) {
      return;
    }

    trackEvent(TRACKING_EVENTS.NC_FILE, {
      action: 'favoriteSet',
      ncFileId: String(ncFileId || ''),
      versionNumber: version?.sequenceNumber || 0,
    });

    dispatch(
      setNcFileData({
        ...ncFile,
        versions: ncFile.versions.map((v) => ({
          ...v,
          isFavorite: v.ncFileVersionId === version.ncFileVersionId,
        })),
      })
    );

    setSelectedVersion(version);

    try {
      await dispatch(
        applyNcFileVersionMasterDataChanges({
          ncFileVersion: {
            isActive: version.isActive,
            isFavorite: true,
            ncFileId: ncFile.ncFileId!,
            ncFileVersionId: version.ncFileVersionId,
          },
        })
      );
    } catch (error) {
      console.error('Failed to update favorite NC file version', error);
    }
  };

  const onMarkThisVersionAsInactive = async () => {
    if (!ncFile || !selectedVersion) return;

    dispatch(
      setNcFileData({
        ...ncFile,
        versions: ncFile.versions.map((v) => (v?.ncFileVersionId === selectedVersion?.ncFileVersionId ? { ...v, isActive: false } : v)),
      })
    );

    setSelectedVersion(favoriteOrLatest);

    await dispatch(
      applyNcFileVersionMasterDataChanges({
        ncFileVersion: {
          isActive: false,
          isFavorite: selectedVersion.isFavorite,
          ncFileId: ncFile?.ncFileId!,
          ncFileVersionId: selectedVersion.ncFileVersionId,
        },
      })
    );
  };

  const toggleInactiveVersions = () => {
    setShowOnlyActiveVersions((prev) => !prev);
  };

  // Comparison mode functions
  const enterComparisonMode = (initialVersion?: NCFileVersionType) => {
    setComparisonMode(true);
    setVersionsToCompare(initialVersion ? [initialVersion] : []);
  };

  const exitComparisonMode = () => {
    setComparisonMode(false);
    setVersionsToCompare([]);
  };

  const toggleVersionForComparison = (version: NCFileVersionType) => {
    setVersionsToCompare((prev) => toggleVersionInSelection(prev, version));
  };

  const replaceVersionForComparison = (currentVersionId: NCFileVersionType['ncFileVersionId'], nextVersion: NCFileVersionType) => {
    setVersionsToCompare((prev) => replaceVersionInPair(prev, currentVersionId, nextVersion) ?? prev);
  };

  const startComparison = () => {
    if (versionsToCompare.length !== 2) {
      return;
    }

    const hash1 = versionsToCompare[0].fileContentHash;
    const hash2 = versionsToCompare[1].fileContentHash;

    if (hash1 && hash2 && hash1 === hash2) {
      showIdenticalVersionsAlert();
      return;
    }

    setShowDiffModal(true);
    trackEvent(TRACKING_EVENTS.NC_FILE, {
      action: 'versionsCompared',
      ncFileId: String(ncFileId || ''),
      versionA: versionsToCompare[0]?.sequenceNumber || 0,
      versionB: versionsToCompare[1]?.sequenceNumber || 0,
      totalVersionCount: ncFile?.versions?.length || 0,
    });
  };

  const closeDiffModal = () => {
    setShowDiffModal(false);
  };

  const isResponsiveTwoColumn = useMemo(() => {
    if (isMobileWebViewport) return false;
    if (!enableResponsiveTwoColumnView) return false;

    // Web: use breakpoint. Native tablet: opt-in via prop.
    if (!isWeb && !isPhone && forceTwoColumnOnNativeTablet) return true;

    const meetsWidth = viewportWidth > 0 && viewportWidth <= twoColumnBreakpoint + (isWeb && safeIsMachinesScreenCheck ? scale(LIST_WIDTH) : 0);
    if (isWeb) return meetsWidth;

    return false;
  }, [enableResponsiveTwoColumnView, forceTwoColumnOnNativeTablet, isMobileWebViewport, viewportWidth, twoColumnBreakpoint]);

  const computedTwoColumnView = twoColumnView || isResponsiveTwoColumn;

  useEffect(() => {
    const wasTwoColumn = wasTwoColumnRef.current;
    if (computedTwoColumnView && !wasTwoColumn && rightColumnLevel !== RightColumnLevel.Info) {
      setRightColumnLevel(RightColumnLevel.Info);
    }
    wasTwoColumnRef.current = computedTwoColumnView;
  }, [computedTwoColumnView, rightColumnLevel, setRightColumnLevel]);

  const totalVersionsCount = ncFile?.versions?.length ?? 0;
  const inactiveVersionsCount = ncFile?.versions?.filter((version) => !version.isActive).length ?? 0;
  const hasMultipleVersions = totalVersionsCount > 1;

  return {
    favorite,
    latest,
    favoriteOrLatest,
    machineWhichRanFavoriteOrLatest,
    selectedVersion,
    setSelectedVersion,
    showOnlyActiveVersions,
    setShowOnlyActiveVersions,
    rightColumnLevel,
    setRightColumnLevel,
    otherVersions,
    onSelectVersion,
    onSendToMachine,
    onToggleFavoriteVersion,
    onMarkThisVersionAsInactive,
    toggleInactiveVersions,
    // Comparison mode
    comparisonMode,
    versionsToCompare,
    showDiffModal,
    enterComparisonMode,
    exitComparisonMode,
    toggleVersionForComparison,
    startComparison,
    closeDiffModal,
    replaceVersionForComparison,
    // New version upload
    onUploadNewVersion,
    // Layout
    isMobileWebViewport,
    computedTwoColumnView,
    inactiveVersionsCount,
    hasMultipleVersions,
  };
};
